// src/lib/supabase.js
// --- SUPABASE CLIENT ----------------------------------------------------------
// SETUP: Ersetze die beiden Werte unten mit deinen Supabase-Projektdaten
// → https://app.supabase.com → Dein Projekt → Settings → API
import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://DEIN-PROJEKT.supabase.co';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'DEIN-ANON-KEY';

// Lock-Override: navigator.locks-Deadlock in supabase-js v2.39 vermeiden.
// Wir nutzen einen einfachen In-Memory-Lock statt Web Locks, damit
// auth-pflichtige Queries nie auf einen blockierten Web-Lock warten.
let lockPromise = Promise.resolve();
const inMemoryLock = (_name, _acquireTimeout, fn) => {
  const next = lockPromise.then(() => fn());
  // Schluck Fehler im Lock-Promise damit nachfolgende Locks nicht blockieren
  lockPromise = next.catch(() => {});
  return next;
};

// --- ZEITGRENZE FUER JEDE ANFRAGE --------------------------------------------
// Anna-Lena am 11.09.2026: "es darf sich nicht aufhaengen". Eine Pruefung aller
// Dateien fand 54 Anfragen ohne Zeitgrenze - im Funkloch haette jede davon
// ewig auf Antwort warten koennen. Statt jede einzeln zu flicken, bekommt hier
// JEDE Anfrage an die Datenbank eine Zeitgrenze, auch kuenftige.
//
// 60 s, weil einzelne Stellen bewusst so lange warten (Abschluss mit
// Unterschrift). Fotos 120 s. Die Meldung enthaelt "timeout" - daran erkennt
// die Warteschlange (istNetzproblem) ein Netzproblem und MERKT sich die
// Antwort des Monteurs, statt sie zu verwerfen.
const ZEITGRENZE_MS = 60000;
const ZEITGRENZE_FOTOS_MS = 120000;

function fetchMitZeitgrenze(input, init = {}) {
  const adresse = typeof input === 'string' ? input : (input?.url || '');
  const ms = adresse.includes('/storage/v1/') ? ZEITGRENZE_FOTOS_MS : ZEITGRENZE_MS;
  const ctrl = new AbortController();
  // Bricht der Aufrufer selbst ab, geben wir das weiter.
  const aussen = init.signal;
  if (aussen) {
    if (aussen.aborted) ctrl.abort();
    else aussen.addEventListener('abort', () => ctrl.abort(), { once: true });
  }
  let abgelaufen = false;
  const timer = setTimeout(() => { abgelaufen = true; ctrl.abort(); }, ms);
  return fetch(input, { ...init, signal: ctrl.signal })
    .catch((err) => {
      if (abgelaufen) {
        throw new Error('Die Verbindung hat zu lange gedauert (timeout nach '
          + Math.round(ms / 1000) + ' s). Kein Netz?');
      }
      throw err;
    })
    .finally(() => clearTimeout(timer));
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  global: {
    fetch: fetchMitZeitgrenze,
  },
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    lock: inMemoryLock,
  },
  realtime: {
    params: { eventsPerSecond: 2 },
  },
});

// --- TIMEOUT-HELPER ----------------------------------------------------------
// Wrappt eine Supabase-Promise mit hartem Timeout, damit "speichert..." auf dem
// Handy bei schlechter Verbindung nicht ewig hängenbleibt. Wirft einen Fehler
// mit verständlicher deutscher Meldung, wenn die Zeit überschritten wird.
export function withTimeout(promise, ms = 15000, label = 'Speichern') {
  let timer;
  const timeoutPromise = new Promise((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error(`${label} hat zu lange gedauert (>${Math.round(ms/1000)}s). Bitte Internet prüfen und nochmal versuchen.`));
    }, ms);
  });
  return Promise.race([
    Promise.resolve(promise).finally(() => clearTimeout(timer)),
    timeoutPromise,
  ]);
}

// --- ALLE PRUEFPUNKTE EINES PROTOKOLLS ----------------------------------------
// Die Tabelle protokoll_modul_pruefpunkte hat KEINE Spalte protokoll_id - ein
// Pruefpunkt haengt nur ueber sein Kapitel (protokoll_modul_id) am Protokoll.
// Bis zum 11.09.2026 fragten drei Stellen trotzdem nach protokoll_id: Der
// Sicherheitsvorschlag erschien deshalb nie, und die Lueckenpruefung vor dem
// Abschliessen fand nie einen offenen Punkt. Beides still, ohne Fehlermeldung.
// Deshalb gibt es genau EINEN Weg: erst die Kapitel, dann deren Pruefpunkte.
export async function pruefpunkteDesProtokolls(protokollId, spalten = '*') {
  const { data: kapitel, error: e1 } = await withTimeout(
    supabase.from('protokoll_module').select('id').eq('protokoll_id', protokollId),
    20000, 'Kapitel laden'
  );
  if (e1) throw e1;
  const ids = (kapitel || []).map(k => k.id);
  if (ids.length === 0) return [];
  const { data, error: e2 } = await withTimeout(
    supabase.from('protokoll_modul_pruefpunkte').select(spalten).in('protokoll_modul_id', ids),
    20000, 'Prüfpunkte laden'
  );
  if (e2) throw e2;
  return data || [];
}

// --- AUTH HELPERS -------------------------------------------------------------
export const auth = {
  // Login
  signIn: (email, password) =>
    supabase.auth.signInWithPassword({ email, password }),

  // Logout
  signOut: () => supabase.auth.signOut(),

  // Passwort-Vergessen E-Mail senden
  resetPassword: (email) =>
    supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    }),

  // Neues Passwort setzen (nach Reset-Link)
  updatePassword: (newPassword) =>
    supabase.auth.updateUser({ password: newPassword }),

  // Session
  getSession: () => supabase.auth.getSession(),

  // Auth-State-Listener
  onAuthStateChange: (callback) =>
    supabase.auth.onAuthStateChange(callback),
};

// --- PROTOKOLL HELPERS --------------------------------------------------------
export const db = {
  // Nutzerprofil laden
  getUserProfile: (userId) =>
    supabase.from('user_profiles').select('*').eq('id', userId).single(),

  // --- VNB (Verteilnetzbetreiber) --------------------------------------------
  vnb: {
    list: ({ search = '', limit = 200, offset = 0, onlyActive = true } = {}) => {
      let q = supabase.from('vnb').select('*', { count: 'exact' }).order('name');
      if (onlyActive) q = q.eq('active', true);
      if (search) {
        const s = search.replace(/[%_]/g, '\\$&');
        q = q.or(`name.ilike.%${s}%,ort.ilike.%${s}%,ansprechpartner.ilike.%${s}%`);
      }
      return q.range(offset, offset + limit - 1);
    },
    listAll: () => supabase.from('vnb').select('*').eq('active', true).order('name'),
    get: (id) => supabase.from('vnb').select('*').eq('id', id).single(),
    save: (vnb) => supabase.from('vnb').upsert(vnb).select().single(),
    delete: (id) => supabase.from('vnb').update({ active: false }).eq('id', id),
  },

  // --- KUNDEN ----------------------------------------------------------------
  kunden: {
    // Suchbare, paginierte Liste
    list: ({ search = '', limit = 50, offset = 0, onlyActive = true } = {}) => {
      let q = supabase
        .from('kunden')
        .select('*', { count: 'exact' })
        .order('name');
      if (onlyActive) q = q.eq('active', true);
      if (search) {
        const s = search.replace(/[%_]/g, '\\$&');
        q = q.or(
          `name.ilike.%${s}%,kunden_nr.ilike.%${s}%,ort.ilike.%${s}%,ansprechpartner.ilike.%${s}%`
        );
      }
      return q.range(offset, offset + limit - 1);
    },
    get: (id) => supabase.from('kunden').select('*').eq('id', id).single(),
    save: (kunde) => supabase.from('kunden').upsert(kunde).select().single(),
    delete: (id) => supabase.from('kunden').delete().eq('id', id),
  },

  // --- STATIONEN -------------------------------------------------------------
  stationen: {
    // Stationen eines Kunden
    listByKunde: (kundeId) =>
      supabase
        .from('stationen')
        .select('*')
        .eq('kunde_id', kundeId)
        .eq('active', true)
        .order('bezeichnung'),
    // Alle Stationen mit Kunden-Namen (paginiert)
    //
    // Die Suche geht bewusst breit: Im Buero wird meistens nach dem KUNDEN
    // gesucht ("Artmann"), nicht nach der Stationsbezeichnung ("H219").
    // Weil PostgREST nicht ueber eine verknuepfte Tabelle filtern kann, werden
    // die passenden Kunden vorher einzeln gesucht und ihre IDs mitgegeben.
    listAll: async ({ search = '', limit = 100, offset = 0 } = {}) => {
      const bauen = (kundenIds) => {
        let q = supabase
          .from('stationen')
          .select('*, kunden(id,name,kunden_nr)', { count: 'exact' })
          .eq('active', true)
          .order('bezeichnung');
        if (search) {
          const s = search.replace(/[%_,()]/g, '');
          const teile = [
            `bezeichnung.ilike.%${s}%`,
            `stations_nr.ilike.%${s}%`,
            `standort_ort.ilike.%${s}%`,
            `standort_strasse.ilike.%${s}%`,
            `standort_plz.ilike.%${s}%`,
            `vnb_stationsbezeichnung.ilike.%${s}%`,
            `objekt_name.ilike.%${s}%`,
          ];
          if (kundenIds && kundenIds.length) teile.push(`kunde_id.in.(${kundenIds.join(',')})`);
          q = q.or(teile.join(','));
        }
        return q.range(offset, offset + limit - 1);
      };

      if (!search) return bauen(null);

      const s = search.replace(/[%_,()]/g, '');
      // Auf 50 begrenzt: Sonst wird die URL bei einem Allerweltswort zu lang.
      const { data: kundenTreffer } = await supabase
        .from('kunden').select('id')
        .or(`name.ilike.%${s}%,kunden_nr.ilike.%${s}%`)
        .limit(50);
      return bauen((kundenTreffer || []).map(k => k.id));
    },
    get: (id) =>
      supabase.from('stationen').select('*, kunden(*)').eq('id', id).single(),
    // Mitgeladene Verknuepfungen (get() holt kunden(*) dazu) sind keine
    // Spalten. Kamen sie mit, lehnte die Datenbank die GANZE Station ab -
    // im Protokoll liessen sich Bezeichnung und Anschrift deshalb nie aendern.
    save: (station) => {
      const s = { ...station };
      for (const k of ['kunden', 'station_konfiguration', 'station_trafos', 'vnb']) delete s[k];
      return supabase.from('stationen').upsert(s).select().single();
    },
    delete: (id) =>
      supabase.from('stationen').update({ active: false }).eq('id', id),
  },

  // --- STATION KONFIGURATION -------------------------------------------------
  // (Zahlen-Hilfen zahlAusEingabe / trafoBereinigen stehen unterhalb von db.)
  stationKonfig: {
    get: (stationId) =>
      supabase
        .from('station_konfiguration')
        .select('*')
        .eq('station_id', stationId)
        .maybeSingle(),
    // Leere Auswahlfelder gehen als NULL raus. Die Datenbank laesst bei diesen
    // drei Spalten nur die festgelegten Werte oder NULL zu; ein leerer Text ist
    // keines von beidem und wird mit "violates check constraint" abgelehnt.
    // Hier zentral, damit es an jeder Speicherstelle gilt.
    save: (config) => {
      const bereinigt = konfigBereinigen(config);
      return supabase
        .from('station_konfiguration')
        .upsert(bereinigt, { onConflict: 'station_id' })
        .select()
        .single();
    },
  },

  // --- MS-FELDER -------------------------------------------------------------
  msFelder: {
    listByStation: (stationId) =>
      supabase
        .from('station_ms_felder')
        .select('*')
        .eq('station_id', stationId)
        .eq('active', true)
        .order('position'),
  },

  // --- TRAFOS ----------------------------------------------------------------
  trafos: {
    listByStation: (stationId) =>
      supabase
        .from('station_trafos')
        .select('*')
        .eq('station_id', stationId)
        .eq('active', true)
        .order('position'),
  },

  // --- WANDLER (Strom + Spannung) --------------------------------------------
  wandler: {
    listByStation: (stationId) =>
      supabase
        .from('station_wandler')
        .select('*')
        .eq('station_id', stationId)
        .order('wandler_typ')
        .order('position'),
  },

};

// --- ZAHLEN AUS EINGABEFELDERN -----------------------------------------------
// Felder mit Vorschlagsliste liefern TEXT: "0,4", "630 kVA", "20kV". Die
// Datenbank-Spalten sind Zahlen und lehnen so etwas ab ("invalid input syntax
// for type numeric") - und zwar die GANZE Zeile. Die Vorschlagsliste selbst
// bot "0,4" an. Hier wird daraus 0.4; leer wird NULL.
export function zahlAusEingabe(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const m = String(v).trim().replace(/\s+/g, '').replace(',', '.').match(/^-?\d+(\.\d+)?/);
  return m ? Number(m[0]) : null;
}

const TRAFO_ZAHLFELDER = [
  'leistung_kva', 'os_spannung_v', 'us_spannung_v', 'uk_prozent', 'os_anzapfungen_prozent',
  'gesamt_gewicht_kg', 'oel_gewicht_kg', 'gesamt_oel_gewicht_kg', 'herstellungsjahr',
];
// Spalten vom Typ Ganzzahl: "1998,5" lehnte die Datenbank ab - und zwar die
// ganze Zeile. Deshalb gerundet.
const GANZZAHL_FELDER = ['herstellungsjahr', 'baujahr', 'stations_baujahr', 'ms_anlage_anzahl_felder', 'eichjahr',
  'anzahl_ms_raeume', 'anzahl_ns_raeume', 'anzahl_ms_ns_raeume', 'anzahl_trafokammern'];
function ganzzahlen(k) {
  for (const f of GANZZAHL_FELDER) {
    if (f in k && k[f] != null && k[f] !== '') {
      const n = zahlAusEingabe(k[f]);
      k[f] = n == null ? null : Math.round(n);
    }
  }
  return k;
}

// Stationskonfiguration vor dem Speichern - EINE Stelle fuer Formular,
// Protokoll (Warteschlange) und db.stationKonfig.save. Leere Auswahlfelder
// als NULL (CHECK-Regeln), Spannung als Zahl, Ganzzahlen gerundet.
export function konfigBereinigen(config) {
  const k = { ...config };
  delete k.created_at;
  delete k.updated_at;
  for (const feld of ['stations_typ', 'ms_anlage_isolierart', 'verrechnungsmessung']) {
    if (k[feld] === '') k[feld] = null;
  }
  if ('ms_anlage_betriebsspannung_kv' in k) k.ms_anlage_betriebsspannung_kv = zahlAusEingabe(k.ms_anlage_betriebsspannung_kv);
  if ('erder_grenzwert_ohm' in k) k.erder_grenzwert_ohm = zahlAusEingabe(k.erder_grenzwert_ohm);
  return ganzzahlen(k);
}

// Allgemeine Zeile (MS-Feld, Wandler, Station): Zeitstempel raus, Ganzzahlen runden.
export function zeileBereinigen(z) {
  const k = { ...z };
  delete k.created_at;
  delete k.updated_at;
  return ganzzahlen(k);
}

// Nur die Felder, die sich gegenueber "vorher" geaendert haben.
export function geaenderteFelder(neu, vorher) {
  const patch = {};
  for (const [k, v] of Object.entries(neu || {})) {
    if (JSON.stringify(v ?? null) !== JSON.stringify((vorher || {})[k] ?? null)) patch[k] = v;
  }
  return patch;
}

// Trafo-Zeile vor dem Speichern: Zahlenfelder als Zahl, Zeitstempel raus
// (setzt die Datenbank selbst).
export function trafoBereinigen(t) {
  const k = { ...t };
  for (const f of TRAFO_ZAHLFELDER) if (f in k) k[f] = zahlAusEingabe(k[f]);
  delete k.created_at;
  delete k.updated_at;
  return ganzzahlen(k);
}

